import React, {useState, useCallback} from 'react';
import {View, Text} from 'react-native';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import {MaterialIcons} from '@expo/vector-icons';
import styles, {
  lockIconSize,
  teamAccent,
  bandCustomerAccent
} from '../../../style/adminTools';
import useLayoutTier from './useLayoutTier';
import SectionHeader from './SectionHeader';
import CollapsibleGroup from './CollapsibleGroup';
import SettingToggleRow from './SettingToggleRow';
import SettingInputRow from './SettingInputRow';

const INPUT_TYPES = ['number', 'text', 'select'];

const groupDefinitions = definitions => {
  const groups = new Map();
  definitions.forEach(definition => {
    const id = definition.groupId || definition.group;
    if (!groups.has(id)) groups.set(id, {name: definition.group, rows: []});
    groups.get(id).rows.push(definition);
  });
  return Array.from(groups, ([id, {name, rows}]) => ({id, name, rows}));
};

const hasGroup = (collection, name) =>
  collection instanceof Set ? collection.has(name) : !!collection[name];

const SettingsSection = ({
  band = 'customer',
  title,
  description,
  railLabel,
  chipLabel,
  definitions,
  values = {},
  onSave,
  expandedGroups,
  onToggleGroup,
  defaultExpanded = false,
  dimmedGroups,
  groupDescriptions = {},
  groupBadges = {},
  groupCountLabels = {},
  matchKeys,
  highlightQuery,
  renderCustom,
  testID = 'settings-section'
}) => {
  const tier = useLayoutTier();
  const wide = tier === 'wide';
  const mobile = tier === 'mobile';
  const [saveErrors, setSaveErrors] = useState({});

  const handleSaveState = useCallback((key, {error}) => {
    setSaveErrors(prev => {
      if (!!prev[key] === !!error) return prev;
      const next = {...prev};
      if (error) next[key] = error;
      else delete next[key];
      return next;
    });
  }, []);
  const team = band === 'team';

  const renderRow = definition => {
    const {key, label, tooltip, type, options, min, max, disabled, locked} =
      definition;
    const isMatch = Boolean(
      definition.highlight || (matchKeys && matchKeys.has(key))
    );
    if (type === 'custom' && renderCustom) {
      const node = renderCustom(definition, {
        value: values[key],
        onSave,
        onSaveStateChange: handleSaveState,
        highlight: isMatch,
        highlightQuery
      });
      if (node) return node;
    }
    if (type === 'toggle') {
      return (
        <SettingToggleRow
          key={key}
          settingKey={key}
          label={label}
          tooltip={tooltip}
          value={values[key]}
          onSave={onSave}
          disabled={disabled}
          disabledReason={definition.disabledReason}
          highlight={isMatch}
          highlightQuery={highlightQuery}
          onSaveStateChange={handleSaveState}
        />
      );
    }
    if (INPUT_TYPES.includes(type)) {
      return (
        <SettingInputRow
          key={key}
          settingKey={key}
          label={label}
          tooltip={tooltip}
          type={type}
          value={values[key]}
          options={options}
          min={min}
          max={max}
          onSave={onSave}
          disabled={disabled}
          locked={locked}
          highlight={isMatch}
          highlightQuery={highlightQuery}
          onSaveStateChange={handleSaveState}
        />
      );
    }
    return (
      <View key={key} style={styles.settingRow} testID={`setting-${key}`}>
        <Text style={styles.settingErrorText}>
          {I18n.t('adminTools.unknownSettingType', {type, key})}
        </Text>
      </View>
    );
  };

  const renderCell = definition => (
    <View
      key={definition.key}
      style={
        wide && definition.type === 'toggle'
          ? styles.rowCellHalf
          : styles.rowCellFull
      }
      testID={`cell-${definition.key}`}
    >
      {renderRow(definition)}
    </View>
  );

  return (
    <View
      style={[
        styles.band,
        mobile ? styles.bandStacked : null,
        team ? styles.bandTeam : styles.bandCustomer
      ]}
      testID={testID}
    >
      {mobile ? (
        <View
          style={[
            styles.bandStrip,
            team ? styles.bandRailTeam : styles.bandRailCustomer
          ]}
          testID={`${testID}-strip-${band}`}
        >
          <MaterialIcons
            name={team ? 'lock' : 'visibility'}
            size={lockIconSize}
            color={team ? teamAccent : bandCustomerAccent}
            testID={`${testID}-strip-icon`}
          />
          {railLabel ? (
            <Text
              style={[
                styles.bandStripLabel,
                team ? styles.bandRailLabelTeam : styles.bandRailLabelCustomer
              ]}
              testID={`${testID}-strip-label`}
            >
              {railLabel}
            </Text>
          ) : null}
        </View>
      ) : (
        <View
          style={[
            styles.bandRail,
            team ? styles.bandRailTeam : styles.bandRailCustomer
          ]}
          testID={`${testID}-rail-${band}`}
        >
          {railLabel ? (
            <Text
              style={[
                styles.bandRailLabel,
                team ? styles.bandRailLabelTeam : styles.bandRailLabelCustomer
              ]}
              testID={`${testID}-rail-label`}
            >
              {railLabel}
            </Text>
          ) : null}
        </View>
      )}
      <View style={styles.bandContent}>
        {title ? (
          <SectionHeader
            title={title}
            description={description}
            chip={mobile ? undefined : chipLabel}
            band={band}
            testID={`${testID}-header`}
          />
        ) : null}
        {groupDefinitions(definitions).map(({id, name, rows}) => (
          <View
            key={id}
            style={styles.groupCellFull}
            testID={`group-cell-${id}`}
          >
            <CollapsibleGroup
              title={name}
              description={groupDescriptions[id]}
              badge={groupBadges[id]}
              countLabel={groupCountLabels[id]}
              count={rows.length}
              errorCount={rows.filter(row => saveErrors[row.key]).length}
              band={band}
              defaultExpanded={defaultExpanded}
              dimmed={dimmedGroups ? hasGroup(dimmedGroups, id) : false}
              {...(expandedGroups
                ? {expanded: hasGroup(expandedGroups, id)}
                : {})}
              onToggle={
                onToggleGroup ? next => onToggleGroup(id, next) : undefined
              }
              testID={`group-${id}`}
            >
              <View style={styles.rowGrid}>{rows.map(renderCell)}</View>
            </CollapsibleGroup>
          </View>
        ))}
      </View>
    </View>
  );
};

SettingsSection.propTypes = {
  band: PropTypes.oneOf(['team', 'customer']),
  title: PropTypes.string,
  description: PropTypes.string,
  railLabel: PropTypes.string,
  chipLabel: PropTypes.string,
  definitions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      group: PropTypes.string.isRequired,
      groupId: PropTypes.string,
      label: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      type: PropTypes.string.isRequired,
      options: PropTypes.array,
      min: PropTypes.number,
      max: PropTypes.number,
      disabled: PropTypes.bool,
      locked: PropTypes.bool,
      highlight: PropTypes.bool
    })
  ).isRequired,
  values: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  expandedGroups: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.instanceOf(Set)
  ]),
  onToggleGroup: PropTypes.func,
  defaultExpanded: PropTypes.bool,
  dimmedGroups: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.instanceOf(Set)
  ]),
  groupDescriptions: PropTypes.objectOf(PropTypes.string),
  groupBadges: PropTypes.objectOf(PropTypes.string),
  groupCountLabels: PropTypes.objectOf(PropTypes.string),
  matchKeys: PropTypes.instanceOf(Set),
  highlightQuery: PropTypes.string,
  renderCustom: PropTypes.func,
  testID: PropTypes.string
};

export default SettingsSection;
